import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import subscriptionService from '../services/subscriptionApi';
import { useTheme } from '../contexts/ThemeContext';
import { trackInitiateCheckout } from '../utils/analytics';

// Fallback só pra 1ª renderização antes do GET /subscriptions/plan responder —
// o valor real (que pode divergir se SUBSCRIPTION_PRICE_BRL for sobrescrito
// por env var no backend) vem sempre da API, nunca fica só nessa constante.
const DEFAULT_SUBSCRIPTION_PRICE = 49.90;
const DEFAULT_SUBSCRIPTION_TOKENS = 30;

const CheckoutPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme: themeConfig } = useTheme();

  const availablePlans = useMemo(() => {
    const items = themeConfig?.tokens?.items || [];
    const parsePrice = (p?: any): number => {
      const s = typeof p === 'number' ? String(p) : String(p || '').trim();
      if (!s) return 0;
      const cleaned = s.replace(/[^\d.,]/g, '').replace(',', '.');
      const n = Number(cleaned);
      return Number.isNaN(n) ? 0 : n;
    };
    const plans = (items.length ? items : [
      { amount: 30, price: 'R$ 19,90' },
      { amount: 60, price: 'R$ 34,90' },
      { amount: 90, price: 'R$ 49,90' },
    ]).map((it: any) => ({
      amount: Number(it.amount || 0),
      price: parsePrice(it.price),
      originalPrice: it.originalPrice ? parsePrice(it.originalPrice) : 0,
      label: `${Number(it.amount || 0)} Tokens`,
    }));
    return plans.filter(p => p.amount > 0);
  }, [themeConfig]);

  const initialTokens = Number(searchParams.get('tokens') || 0);
  const [selectedTokens, setSelectedTokens] = useState<number>(initialTokens || (availablePlans[0]?.amount || 30));
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'card'>('card');
  const [couponCode, setCouponCode] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [pixData, setPixData] = useState<{ qr_code: string; qr_code_base64: string; payment_id: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponPreview, setCouponPreview] = useState<{ valid: boolean; original_amount: number; amount: number; tokens: number; discount_type: string | null } | null>(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponMsg, setCouponMsg] = useState('');
  const [mode, setMode] = useState<'tokens' | 'subscription'>(
    searchParams.get('mode') === 'subscription' ? 'subscription' : 'tokens'
  );
  const [subscriptionPlan, setSubscriptionPlan] = useState({
    price: DEFAULT_SUBSCRIPTION_PRICE,
    tokens: DEFAULT_SUBSCRIPTION_TOKENS,
  });

  useEffect(() => {
    subscriptionService.getPlan()
      .then(p => setSubscriptionPlan({ price: p.price, tokens: p.tokens_per_cycle }))
      .catch(() => {}); // mantém o fallback se a chamada falhar
  }, []);

  useEffect(() => {
    const tok = Number(searchParams.get('tokens') || 0);
    const list = availablePlans.map(p => p.amount);
    if (tok && list.includes(tok)) {
      setSelectedTokens(tok);
    } else if (!tok && availablePlans.length) {
      setSelectedTokens(availablePlans[0].amount);
    }
  }, [searchParams, availablePlans]);

  const currentPlan = availablePlans.find(p => p.amount === selectedTokens) || availablePlans[0];

  // Cupom vale só pro pacote/código com que foi validado — muda qualquer um dos
  // dois e o preview anterior fica sem sentido, precisa aplicar de novo.
  useEffect(() => {
    setCouponPreview(null);
    setCouponMsg('');
  }, [couponCode, selectedTokens]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponChecking(true);
    setCouponMsg('');
    try {
      const r = await api.get('/payments/mp/coupon-preview', { params: { tokens: selectedTokens, coupon_code: couponCode } });
      setCouponPreview(r.data);
      setCouponMsg(r.data?.valid ? '' : 'Cupom inválido, inativo ou expirado');
    } catch {
      setCouponPreview(null);
      setCouponMsg('Erro ao validar o cupom — tente de novo');
    } finally {
      setCouponChecking(false);
    }
  };

  const displayAmount = couponPreview?.valid ? couponPreview.amount : currentPlan?.price;
  const displayTokens = couponPreview?.valid ? couponPreview.tokens : currentPlan?.amount;

  const formatCpf = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  };

  const startPayment = async () => {
    if (paymentMethod === 'pix') {
      const digits = cpf.replace(/\D/g, '');
      if (digits.length !== 11) {
        setStatus('Informe um CPF válido com 11 dígitos');
        return;
      }
    }
    setLoading(true);
    setStatus('Iniciando pagamento...');
    setPixData(null);
    try {
      if (paymentMethod === 'card') {
        const qs = new URLSearchParams({ tokens: String(selectedTokens), ...(couponCode ? { coupon_code: couponCode } : {}) });
        const r = await api.post(`/payments/mp/create-preference?${qs.toString()}`);
        const d = r.data || {};
        trackInitiateCheckout(Number(d.amount || currentPlan?.price || 0), { method: 'card', mode: 'tokens' });
        setStatus(`Pedido criado. Valor R$ ${Number(d.amount || 0).toFixed(2)}`);
        if (d.init_point && d.init_point !== '#') {
          window.open(d.init_point, '_blank');
        }
      } else {
        const digits = cpf.replace(/\D/g, '');
        const qs = new URLSearchParams({
          tokens: String(selectedTokens),
          payer_cpf: digits,
          ...(couponCode ? { coupon_code: couponCode } : {})
        });
        const r = await api.post(`/payments/mp/create-pix?${qs.toString()}`);
        const d = r.data || {};
        if (!d.qr_code) {
          setStatus('Erro ao gerar PIX — tente novamente');
          return;
        }
        setPixData({ qr_code: d.qr_code, qr_code_base64: d.qr_code_base64, payment_id: d.payment_id });
        trackInitiateCheckout(Number(d.amount || currentPlan?.price || 0), { method: 'pix', mode: 'tokens' });
        setStatus('');
      }
    } catch (e: any) {
      setStatus(e?.response?.data?.detail || 'Falha ao iniciar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const startSubscription = async () => {
    setLoading(true);
    setStatus('Iniciando assinatura...');
    try {
      const d = await subscriptionService.createSubscription();
      if (d.init_point) {
        trackInitiateCheckout(subscriptionPlan.price, { method: 'card', mode: 'subscription' });
        setStatus('Redirecionando para autorização da assinatura...');
        window.open(d.init_point, '_blank');
      } else {
        setStatus('Assinatura criada, mas o link de autorização não veio — tente novamente.');
      }
    } catch (e: any) {
      setStatus(e?.response?.data?.detail || 'Falha ao iniciar assinatura');
    } finally {
      setLoading(false);
    }
  };

  const copyPix = () => {
    if (!pixData?.qr_code) return;
    navigator.clipboard.writeText(pixData.qr_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const tierLabels = ['Iniciante', 'Popular', 'Avançado'];

  return (
    <div className="min-h-screen text-white" style={{ background: 'linear-gradient(135deg, #0f0f23 0%, #1a0a2e 50%, #0f0f23 100%)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm">
          <span>←</span>
          <span>Voltar</span>
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-purple-400 text-lg">✦</span>
          <span className="font-bold text-white">Astralyz</span>
        </div>
        <div className="w-20 flex justify-end">
          <span className="text-xs text-white/30 flex items-center gap-1">🔒 Seguro</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 sm:py-10">
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {mode === 'tokens' ? 'Adquirir Tokens' : 'Assinatura Astralyz'}
          </h1>
          <p className="text-white/50 text-sm">
            {mode === 'tokens'
              ? 'Tokens são debitados por consulta. Nunca expiram.'
              : 'Cobrança mensal recorrente no cartão. Cancele quando quiser.'}
          </p>
        </div>

        {/* Toggle: tokens avulsos x assinatura */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {(['tokens', 'subscription'] as const).map(m => {
            const isActive = mode === m;
            return (
              <button
                key={m}
                onClick={() => { setMode(m); setStatus(''); setPixData(null); }}
                className="rounded-xl py-3 px-4 text-sm font-semibold transition-all duration-200"
                style={isActive ? {
                  background: 'linear-gradient(135deg, rgba(109,40,217,0.4), rgba(124,58,237,0.3))',
                  border: '1px solid rgba(139,92,246,0.7)',
                  color: '#fff',
                } : {
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.5)',
                }}
              >
                {m === 'tokens' ? '🪙 Tokens avulsos' : '✨ Assinatura mensal'}
              </button>
            );
          })}
        </div>

        {/* Plano */}
        {mode === 'tokens' && (
        <div className="rounded-2xl p-4 sm:p-6 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-widest mb-4">Selecione o pacote</h2>
          <div className="grid grid-cols-3 gap-3">
            {availablePlans.map((plan, idx) => {
              const isSelected = plan.amount === selectedTokens;
              const isPopular = idx === 1;
              return (
                <button
                  key={plan.amount}
                  onClick={() => setSelectedTokens(plan.amount)}
                  className="relative rounded-xl p-2 sm:p-4 text-center transition-all duration-200"
                  style={isSelected ? {
                    background: 'linear-gradient(135deg, rgba(109,40,217,0.4), rgba(124,58,237,0.3))',
                    border: '1px solid rgba(139,92,246,0.7)',
                    boxShadow: '0 0 20px rgba(139,92,246,0.25)',
                  } : {
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {isPopular && (
                    <div className="absolute -top-2.5 inset-x-0 flex justify-center">
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ background: 'linear-gradient(to right,#f59e0b,#d97706)', color: '#000', fontSize: '0.6rem' }}>
                        POPULAR
                      </span>
                    </div>
                  )}
                  <div className={`text-xl sm:text-2xl font-extrabold ${isSelected ? 'text-white' : 'text-white/60'}`}>{plan.amount}</div>
                  <div className="text-xs text-white/40 mb-2">tokens</div>
                  {plan.originalPrice > plan.price && (
                    <div className="text-[10px] line-through" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      R$ {plan.originalPrice.toFixed(2)}
                    </div>
                  )}
                  <div className={`text-sm font-bold ${isSelected ? 'text-purple-300' : 'text-white/50'}`}>
                    R$ {plan.price.toFixed(2)}
                  </div>
                  {plan.originalPrice > plan.price && (
                    <div className="text-[9px] font-semibold" style={{ color: '#4ade80' }}>
                      OFERTA
                    </div>
                  )}
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}>
                    {(plan.price / plan.amount).toFixed(2)}/tkn
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Forma de pagamento */}
        {mode === 'tokens' && (
        <div className="rounded-2xl p-4 sm:p-6 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-widest mb-4">Forma de pagamento</h2>
          <div className="grid grid-cols-2 gap-3 mb-5">
            {(['card', 'pix'] as const).map(method => {
              const isActive = paymentMethod === method;
              const label = method === 'card' ? 'Cartão de Crédito' : 'PIX';
              const icon = method === 'card' ? '💳' : '⚡';
              const desc = method === 'card' ? 'via Mercado Pago' : 'Aprovação imediata';
              return (
                <button
                  key={method}
                  onClick={() => { setPaymentMethod(method); setPixData(null); setStatus(''); }}
                  className="rounded-xl p-4 text-left transition-all duration-200"
                  style={isActive ? {
                    background: 'rgba(139,92,246,0.18)',
                    border: '1px solid rgba(139,92,246,0.6)',
                  } : {
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{icon}</span>
                    <span className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-white/60'}`}>{label}</span>
                    {isActive && (
                      <span className="ml-auto w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#7c3aed' }}>
                        <span className="text-white text-xs">✓</span>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/35 pl-7">{desc}</div>
                </button>
              );
            })}
          </div>

          {paymentMethod === 'pix' && (
            <div>
              <div className="flex items-start gap-2 rounded-xl px-4 py-3 mb-4 text-xs" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', color: 'rgba(251,191,36,0.8)' }}>
                <span className="flex-shrink-0 mt-0.5">ℹ️</span>
                <span>
                  O nome do recebedor exibido no seu aplicativo de banco será o nome pessoal do titular da conta — e não o nome Astralyz. Isso é normal e não afeta o processamento do pagamento.
                </span>
              </div>
              <label className="block text-xs text-white/50 mb-2">CPF do pagador</label>
              <input
                type="text"
                value={cpf}
                onChange={e => setCpf(formatCpf(e.target.value))}
                placeholder="000.000.000-00"
                className="w-full px-4 py-3 rounded-xl text-white placeholder-white/25 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
          )}
        </div>
        )}

        {/* Cupom (colapsável) */}
        {mode === 'tokens' && (
        <div className="rounded-2xl mb-5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
          <button
            onClick={() => setCouponOpen(v => !v)}
            className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-sm text-white/50 hover:text-white/80 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span>🏷️</span>
              <span>Tenho um cupom de desconto</span>
            </span>
            <span className="text-xs">{couponOpen ? '▲' : '▼'}</span>
          </button>
          {couponOpen && (
            <div className="px-4 sm:px-6 pb-4 sm:pb-5">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={e => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="CÓDIGO DO CUPOM"
                  className="flex-1 px-4 py-3 rounded-xl text-white placeholder-white/25 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <button
                  onClick={applyCoupon}
                  disabled={couponChecking || !couponCode.trim()}
                  className="px-5 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-opacity"
                  style={{ background: 'rgba(139,92,246,0.5)', border: '1px solid rgba(139,92,246,0.7)' }}
                >
                  {couponChecking ? '...' : 'Aplicar'}
                </button>
              </div>
              {couponPreview?.valid && (
                <p className="text-green-400 text-xs mt-2">
                  ✓ Cupom aplicado{couponPreview.discount_type === 'tokens'
                    ? ` — +${couponPreview.tokens - (currentPlan?.amount || 0)} tokens de bônus`
                    : ` — de R$ ${couponPreview.original_amount.toFixed(2)} por R$ ${couponPreview.amount.toFixed(2)}`}
                </p>
              )}
              {couponMsg && <p className="text-red-400 text-xs mt-2">{couponMsg}</p>}
            </div>
          )}
        </div>
        )}

        {/* Resumo do pedido */}
        {mode === 'tokens' && !pixData && (
          <div className="rounded-2xl p-4 sm:p-6 mb-4" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/60 text-sm">Pacote selecionado</span>
              <span className="text-white font-semibold">{displayTokens} Tokens</span>
            </div>
            {couponPreview?.valid && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-white/60 text-sm">Cupom</span>
                <span className="text-green-400 text-sm font-mono">{couponCode}</span>
              </div>
            )}
            <div className="h-px my-3" style={{ background: 'rgba(139,92,246,0.2)' }} />
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold">Total</span>
              <div className="text-right">
                {(() => {
                  // Referência riscada: preço "de" da promoção, ou o preço cheio quando
                  // é o cupom que está descontando — o que fizer sentido no momento.
                  const strikePrice = couponPreview?.valid
                    ? currentPlan?.price
                    : (!!currentPlan?.originalPrice && currentPlan.originalPrice > (currentPlan.price || 0) ? currentPlan.originalPrice : null);
                  return strikePrice ? (
                    <div className="text-xs line-through" style={{ color: 'rgba(255,255,255,0.35)' }}>
                      R$ {strikePrice.toFixed(2)}
                    </div>
                  ) : null;
                })()}
                <span className="text-xl sm:text-2xl font-bold" style={{ color: '#fbbf24' }}>
                  R$ {displayAmount?.toFixed(2)}
                </span>
              </div>
            </div>
            {couponPreview?.valid ? (
              <div className="text-right text-[11px] font-semibold mt-1" style={{ color: '#4ade80' }}>
                Cupom {couponCode} aplicado
              </div>
            ) : !!currentPlan?.originalPrice && currentPlan.originalPrice > (currentPlan.price || 0) && (
              <div className="text-right text-[11px] font-semibold mt-1" style={{ color: '#4ade80' }}>
                Preço promocional por tempo limitado
              </div>
            )}
          </div>
        )}

        {/* Resumo da assinatura */}
        {mode === 'subscription' && (
          <div className="rounded-2xl p-4 sm:p-6 mb-4" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-white font-semibold">Assinatura Astralyz</span>
              <span className="text-xl sm:text-2xl font-bold" style={{ color: '#fbbf24' }}>
                R$ {subscriptionPlan.price.toFixed(2)}<span className="text-sm text-white/40">/mês</span>
              </span>
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2"><span>🪙</span><span>{subscriptionPlan.tokens} tokens todo mês (não acumulam pro mês seguinte)</span></li>
              <li className="flex items-start gap-2"><span>♾️</span><span><strong className="text-white">Aprofundar leitura</strong> e <strong className="text-white">Perguntar sobre a leitura</strong> ilimitados — nunca gastam token</span></li>
              <li className="flex items-start gap-2"><span>💳</span><span>Cobrança recorrente só no cartão. Cancele quando quiser.</span></li>
            </ul>
          </div>
        )}

        {/* QR Code PIX */}
        {mode === 'tokens' && pixData && (
          <div className="rounded-2xl p-4 sm:p-7 mb-4 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl">⚡</span>
              <h2 className="text-xl font-bold text-white">Pague via PIX</h2>
            </div>
            <p className="text-sm text-white/50 mb-6">Escaneie o QR Code ou copie o código. Confirmação automática após o pagamento.</p>

            {pixData.qr_code_base64 && (
              <div className="flex justify-center mb-5">
                <div className="p-3 rounded-2xl" style={{ background: '#fff' }}>
                  <img
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code PIX"
                    className="w-36 h-36 sm:w-44 sm:h-44 block"
                  />
                </div>
              </div>
            )}

            <div className="rounded-xl p-4 mb-4 text-left" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <div className="text-xs text-white/40 mb-1">Código PIX copia e cola</div>
              <div className="text-xs text-white/70 font-mono break-all leading-relaxed">{pixData.qr_code}</div>
            </div>

            <button
              onClick={copyPix}
              className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200"
              style={copied ? {
                background: 'rgba(34,197,94,0.2)',
                border: '1px solid rgba(34,197,94,0.5)',
                color: '#4ade80',
              } : {
                background: 'linear-gradient(to right, #059669, #10b981)',
                color: '#fff',
                boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
              }}
            >
              {copied ? '✓ Copiado!' : '📋 Copiar código PIX'}
            </button>

            <p className="text-xs text-white/30 mt-4">
              Após o pagamento, seus tokens serão creditados automaticamente.
            </p>
          </div>
        )}

        {/* Status */}
        {status && (
          <div className="rounded-xl px-4 py-3 mb-4 text-sm text-center" style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' }}>
            {status}
          </div>
        )}

        {/* Botão de ação */}
        {mode === 'tokens' && !pixData && (
          <button
            onClick={startPayment}
            disabled={loading}
            className="w-full py-4 rounded-xl font-bold text-base transition-all duration-200"
            style={loading ? {
              background: 'rgba(139,92,246,0.3)',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'not-allowed',
              border: '1px solid rgba(139,92,246,0.2)',
            } : {
              background: 'linear-gradient(to right, #7c3aed, #6d28d9)',
              color: '#fff',
              boxShadow: '0 4px 25px rgba(124,58,237,0.4)',
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin">⟳</span> Processando...
              </span>
            ) : (
              `Pagar R$ ${currentPlan?.price?.toFixed(2)} ${paymentMethod === 'pix' ? 'via PIX' : 'com Cartão'}`
            )}
          </button>
        )}

        {mode === 'subscription' && (
          <button
            onClick={startSubscription}
            disabled={loading}
            className="w-full py-4 rounded-xl font-bold text-base transition-all duration-200"
            style={loading ? {
              background: 'rgba(139,92,246,0.3)',
              color: 'rgba(255,255,255,0.4)',
              cursor: 'not-allowed',
              border: '1px solid rgba(139,92,246,0.2)',
            } : {
              background: 'linear-gradient(to right, #7c3aed, #6d28d9)',
              color: '#fff',
              boxShadow: '0 4px 25px rgba(124,58,237,0.4)',
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin">⟳</span> Processando...
              </span>
            ) : (
              `Assinar por R$ ${subscriptionPlan.price.toFixed(2)}/mês`
            )}
          </button>
        )}

        {/* Trust badges */}
        <div className="flex items-center justify-center flex-wrap gap-3 sm:gap-5 mt-6 sm:mt-8 text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
          <span>🔒 SSL seguro</span>
          <span>·</span>
          <span>🏦 Mercado Pago</span>
          <span>·</span>
          <span>⚡ Tokens na hora</span>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
